import { useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

/**
 * Modal mode for the useModalRoute hook
 */
export type ModalMode = 'create' | 'edit' | 'view' | null;

/**
 * Return type for useModalRoute hook
 */
export interface UseModalRouteReturn {
  /** Resource ID from URL param, null if not present */
  resourceId: string | null;
  /** Current modal mode based on URL */
  mode: ModalMode;
  /** True if modal should be open (create or edit/view mode) */
  isOpen: boolean;
  /** Navigate to basePath/create (preserves query params) */
  openCreate: () => void;
  /** Navigate to basePath/:id/edit (preserves query params) */
  openEdit: (id: string) => void;
  /** Navigate to basePath/:id (preserves query params) - view mode */
  openView: (id: string) => void;
  /** Navigate back to basePath (preserves query params) */
  close: () => void;
}

/**
 * Options for useModalRoute hook
 */
export interface UseModalRouteOptions {
  /**
   * Name of the route param to use for the resource ID.
   * Defaults to 'id'
   */
  paramName?: string;
  /**
   * URL segment for create mode.
   * Defaults to 'create'
   */
  createSegment?: string;
  /**
   * URL segment for edit mode (appended after ID).
   * Defaults to 'edit'
   */
  editSegment?: string;
}

/**
 * Hook for managing modal open/close state via URL path parameter.
 *
 * This hook enables deep linking for modal states by using URL paths
 * instead of local state. Supports create, edit, and view modes.
 *
 * Routes:
 * - basePath           -> modal closed
 * - basePath/create    -> modal open in create mode
 * - basePath/:id       -> modal open in view mode
 * - basePath/:id/edit  -> modal open in edit mode
 *
 * @param basePath - The base path for the route (e.g., '/achats/commandes')
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * const modal = useModalRoute('/achats/commandes', { paramName: 'orderId' });
 *
 * // modal.isOpen = true when in create/edit/view mode
 * // modal.mode = 'create' | 'edit' | 'view' | null
 * // modal.resourceId = 'abc123' if editing/viewing
 * // modal.openCreate() navigates to /achats/commandes/create
 * // modal.openEdit('xyz') navigates to /achats/commandes/xyz/edit
 * // modal.openView('xyz') navigates to /achats/commandes/xyz
 * // modal.close() navigates to /achats/commandes
 * ```
 */
export function useModalRoute(
  basePath: string,
  options: UseModalRouteOptions = {}
): UseModalRouteReturn {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const {
    paramName = 'id',
    createSegment = 'create',
    editSegment = 'edit',
  } = options;

  // Determine mode and resourceId from URL
  const { mode, resourceId } = useMemo(() => {
    const id = params[paramName];
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Check if we're in create mode
    if (lastSegment === createSegment) {
      return { mode: 'create' as ModalMode, resourceId: null };
    }

    // Check if we have an ID
    if (id) {
      // Check if we're in edit mode
      if (lastSegment === editSegment) {
        return { mode: 'edit' as ModalMode, resourceId: id };
      }
      // Otherwise we're in view mode
      return { mode: 'view' as ModalMode, resourceId: id };
    }

    return { mode: null, resourceId: null };
  }, [params, paramName, location.pathname, createSegment, editSegment]);

  // Modal is open when mode is not null
  const isOpen = mode !== null;

  // Build URL with preserved query params
  const buildUrl = useCallback(
    (path: string): string => {
      const queryString = searchParams.toString();
      return queryString ? `${path}?${queryString}` : path;
    },
    [searchParams]
  );

  // Open modal in create mode
  const openCreate = useCallback(() => {
    const url = buildUrl(`${basePath}/${createSegment}`);
    navigate(url);
  }, [basePath, createSegment, buildUrl, navigate]);

  // Open modal in edit mode
  const openEdit = useCallback(
    (id: string) => {
      const url = buildUrl(`${basePath}/${id}/${editSegment}`);
      navigate(url);
    },
    [basePath, editSegment, buildUrl, navigate]
  );

  // Open modal in view mode
  const openView = useCallback(
    (id: string) => {
      const url = buildUrl(`${basePath}/${id}`);
      navigate(url);
    },
    [basePath, buildUrl, navigate]
  );

  // Close modal by navigating back to basePath
  const close = useCallback(() => {
    const url = buildUrl(basePath);
    navigate(url);
  }, [basePath, buildUrl, navigate]);

  return {
    resourceId,
    mode,
    isOpen,
    openCreate,
    openEdit,
    openView,
    close,
  };
}
